import { eq, not, or } from 'tio-ui/utilities';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import Component from '@glimmer/component';
import type RouterService from '@ember/routing/router-service';
import { PhArrowLeft, PhArrowRight } from 'ember-phosphor-icons';
import Button from './button.gts';

const DEFAULT_RANGE = 2;
const DEFAULT_PAGE_SIZE = 50;

interface PaginatorButtonSignature {
  Args: {
    active?: boolean;
    page: number;
  };
  Blocks: {
    default: [];
  };
  Element: HTMLButtonElement;
}

class PaginatorButton extends Component<PaginatorButtonSignature> {
  get baseClasses() {
    return [
      'rounded-lg',
      'px-4',
      'py-2',
      'hover:bg-tio-gray-200',
      'dark:hover:bg-gray-600',
      'disabled:bg-none',
      'h-8',
      'flex',
      'items-center',
      'justify-center',
      'min-w-8',
    ];
  }

  get ariaLabel() {
    const { page } = this.args;
    return 'Goto Page ' + page;
  }

  get className() {
    const { active } = this.args;
    if (active) return [...this.baseClasses, 'bg-tio-gray-100', 'dark:bg-gray-700'].join(' ');
    return this.baseClasses.join(' ');
  }

  <template>
    <button aria-label={{this.ariaLabel}} class={{this.className}} ...attributes type="button">
      {{yield}}
    </button>
  </template>
}

export interface PaginatorSignature {
  Args: {
    totalItems: number;
    pageSize?: number;
    currentPage?: number;
    onChange?: (page: number) => void;
    range?: number;
    summary?: boolean;
    simple?: boolean;
  };
  Element: HTMLElement;
}

export class Paginator extends Component<PaginatorSignature> {
  @service declare router: RouterService;

  get currentPage() {
    const { currentPage } = this.args;
    return Number(currentPage) || Number(this.router.currentRoute?.queryParams['page']) || 1;
  }

  get firstPage() {
    return 1;
  }

  get lastPage() {
    const { totalItems, pageSize = DEFAULT_PAGE_SIZE } = this.args;
    return Math.ceil(totalItems / pageSize) || 1;
  }

  get previousPage() {
    return this.currentPage - 1;
  }

  get nextPage() {
    return this.currentPage + 1;
  }

  get hasPreviousPage() {
    return this.currentPage > this.firstPage;
  }

  get hasNextPage() {
    return this.currentPage < this.lastPage;
  }

  get dynamicPages() {
    const { range = DEFAULT_RANGE } = this.args;
    const { currentPage, firstPage, lastPage } = this;

    const firstPossiblePage = currentPage - 2 * range;
    const lastPossiblePage = currentPage + 2 * range;

    const possiblePages = Array.from(Array(lastPossiblePage - firstPossiblePage)).reduce(
      (currentPages, _el, index) => [...currentPages, index + firstPossiblePage],
      []
    );

    const topOffset = lastPage - 1 - currentPage;
    const bottomOffset = currentPage - (firstPage + 1);

    const padTop = Math.max(range - bottomOffset, 0);
    const padBottom = Math.max(range - topOffset, 0);

    const bottomBound = currentPage - range - padBottom;
    const topBound = currentPage + range + padTop;

    return possiblePages
      .filter((page: number) => firstPage < page && page < lastPage)
      .filter((page: number) => bottomBound <= page && page <= topBound);
  }

  get firstItemOrdinal() {
    const { currentPage } = this;
    const { pageSize = DEFAULT_PAGE_SIZE } = this.args;
    return (currentPage - 1) * pageSize + 1;
  }

  get lastItemOrdinal() {
    const { currentPage } = this;
    const { totalItems, pageSize = DEFAULT_PAGE_SIZE } = this.args;
    return Math.min(currentPage * pageSize, totalItems);
  }

  get firstPageInDynamicPages() {
    return this.dynamicPages.includes(this.firstPage);
  }

  get showStartEllipsis() {
    if (!this.dynamicPages.length) return false;
    return !this.firstPageInDynamicPages && !this.dynamicPages.includes(this.firstPage + 1);
  }

  get lastPageInDynamicPages() {
    return this.dynamicPages.includes(this.lastPage);
  }

  get showEndEllipsis() {
    if (!this.dynamicPages.length) return false;
    return !this.lastPageInDynamicPages && !this.dynamicPages.includes(this.lastPage - 1);
  }

  handlePageClick = (page: number) => {
    const { onChange } = this.args;
    if (onChange) {
      onChange(page);
    } else {
      this.router.transitionTo({ queryParams: { page } });
    }
  };

  <template>
    {{#if @simple}}
      {{! Simple mode: Previous/Next buttons with summary text }}
      <section class="flex justify-center items-center text-tio-gray-700 py-3" ...attributes>
        <div class="flex items-center gap-2">
          <Button
            @appearance="minimal"
            @isDisabled={{not this.hasPreviousPage}}
            {{on "click" (fn this.handlePageClick this.previousPage)}}
            aria-label={{t "common.goto_previous_page"}}
          >
            «
            {{t "common.previous"}}
          </Button>
          <Button
            @appearance="minimal"
            @isDisabled={{not this.hasNextPage}}
            {{on "click" (fn this.handlePageClick this.nextPage)}}
            aria-label={{t "common.goto_next_page"}}
          >
            {{t "common.next"}}
            »
          </Button>
        </div>
      </section>
    {{else}}
      {{! Full mode: Complete pagination with page numbers }}
      <section
        class="flex flex-col md:flex-row justify-between items-center text-tio-gray-700 py-4"
        ...attributes
      >
        {{#if @summary}}
          <div class="mb-2 md:mb-0">
            {{t
              "common.showing_of_results"
              start=this.firstItemOrdinal
              end=this.lastItemOrdinal
              total=@totalItems
              htmlSafe=true
            }}
          </div>
        {{/if}}
        <nav role="navigation" aria-label={{t "common.pagination_navigation"}}>
          <ul class="flex items-center gap-2">
            <li>
              <PaginatorButton
                {{on "click" (fn this.handlePageClick this.previousPage)}}
                @page={{this.previousPage}}
                disabled={{eq this.currentPage this.firstPage}}
                aria-disabled={{eq this.currentPage this.firstPage}}
                role="link"
                aria-label={{t "common.goto_previous_page"}}
              >
                <span class="sr-only">{{t "common.previous"}}</span>
                <PhArrowLeft @size="16" />
              </PaginatorButton>
            </li>
            {{#unless this.firstPageInDynamicPages}}
              <li>
                <PaginatorButton
                  @active={{eq this.currentPage this.firstPage}}
                  @page={{this.firstPage}}
                  {{on "click" (fn this.handlePageClick this.firstPage)}}
                >
                  {{this.firstPage}}
                </PaginatorButton>
              </li>
            {{/unless}}
            {{#if this.showStartEllipsis}}
              <span class="text-tio-gray-500 px-2">...</span>
            {{/if}}
            {{#each this.dynamicPages as |page|}}
              <li>
                <PaginatorButton
                  @page={{page}}
                  @active={{eq this.currentPage page}}
                  {{on "click" (fn this.handlePageClick page)}}
                >
                  {{page}}
                </PaginatorButton>
              </li>
            {{/each}}
            {{#if this.showEndEllipsis}}
              <span class="text-tio-gray-500 px-2">...</span>
            {{/if}}
            {{#if (not (or this.lastPageInDynamicPages (eq this.lastPage 1)))}}
              <li>
                <PaginatorButton
                  @page={{this.lastPage}}
                  @active={{eq this.currentPage this.lastPage}}
                  {{on "click" (fn this.handlePageClick this.lastPage)}}
                >
                  {{this.lastPage}}
                </PaginatorButton>
              </li>
            {{/if}}
            <li>
              <PaginatorButton
                @page={{this.nextPage}}
                {{on "click" (fn this.handlePageClick this.nextPage)}}
                disabled={{eq this.currentPage this.lastPage}}
                aria-disabled={{eq this.currentPage this.lastPage}}
                role="link"
                aria-label={{t "common.goto_next_page"}}
              >
                <span class="sr-only">{{t "common.next"}}</span>
                <PhArrowRight @size="16" />
              </PaginatorButton>
            </li>
          </ul>
        </nav>
      </section>
    {{/if}}
  </template>
}

export default Paginator;
